import { useCallback, useEffect, useState } from "react";
import { TacoAssetType } from "@amzn/afxtaco-types";
import { logger } from "@amzn/afxtaco-utilities";
import { getAssetDownloadUrl } from "../api/assets/assets";

export enum DiagramSvgStatus {
  /** No project selected — resolution not attempted. */
  IDLE = "idle",
  /** Actively resolving the presigned URL (may trigger a server-side render). */
  LOADING = "loading",
  /** URL resolved successfully. */
  READY = "ready",
  /** Resolution failed (render error, network failure, etc.). */
  ERROR = "error",
}

export interface UseDiagramSvgResult {
  /**
   * Object URL (`blob:`) for the current diagram SVG, or null until ready.
   *
   * This is a locally-created blob URL, NOT the S3 presigned URL: the page's
   * CSP allows images from `blob:` but not from the S3 origin, so the bytes are
   * fetched and wrapped in a blob URL (see the effect below). It is only valid
   * within this document and is revoked on re-resolve/unmount, so don't persist
   * it or pass it across contexts.
   */
  url: string | null;
  status: DiagramSvgStatus;
  /** Re-resolve the SVG (re-triggers server-side render-if-stale). */
  refresh: () => void;
}

/**
 * Reactive entry point for obtaining the project's rendered architecture
 * diagram SVG as a displayable image URL.
 *
 * Resolves the SVG's presigned S3 URL via `getAssetDownloadUrl` (which triggers
 * server-side render-if-stale and cache reuse), then fetches the bytes and
 * exposes them as a `blob:` object URL suitable for `<img src>` under the app's
 * CSP. Owns the loading/error state and a `refresh()` so any component that
 * needs to show the diagram image (markdown embed, inline preview, a future
 * panel) can consume it without duplicating the fetch/loading/error handling.
 *
 * @param projectId - the project whose diagram SVG to resolve
 */
export const useDiagramSvg = (
  projectId: string | undefined,
): UseDiagramSvgResult => {
  const [url, setUrl] = useState<string | null>(null);
  const [status, setStatus] = useState<DiagramSvgStatus>(
    projectId ? DiagramSvgStatus.LOADING : DiagramSvgStatus.IDLE,
  );
  // Bumped by refresh() to force a re-resolve.
  const [refreshCounter, setRefreshCounter] = useState(0);

  const refresh = useCallback(() => setRefreshCounter((c) => c + 1), []);

  useEffect(() => {
    if (!projectId) {
      setStatus(DiagramSvgStatus.IDLE);
      setUrl(null);
      return;
    }

    let cancelled = false;
    // Track the blob URL created below so we can revoke it on cleanup.
    let objectUrl: string | null = null;
    setStatus(DiagramSvgStatus.LOADING);
    setUrl(null);

    // Resolve the presigned URL, then fetch the bytes and wrap them in a
    // blob: URL. We cannot point <img src> at the S3 presigned URL directly:
    // the page's CSP only allows `img-src 'self' data: blob:` (the S3 origin
    // is allowed for `connect-src`/fetch, not for image loads). Fetching to a
    // blob and rendering the blob URL is the same pattern useProjects.ts uses
    // for every other asset, and is what keeps this within the CSP.
    (async () => {
      try {
        const response = await getAssetDownloadUrl({
          projectId,
          assetType: TacoAssetType.ARCHITECTURE_DIAGRAM_SVG,
        });
        const presignedUrl = response?.data?.presignedUrl;
        if (!presignedUrl) {
          if (!cancelled) setStatus(DiagramSvgStatus.ERROR);
          return;
        }

        const assetResponse = await fetch(presignedUrl);
        if (!assetResponse.ok) {
          throw new Error(
            `Failed to fetch diagram SVG: ${assetResponse.status}`,
          );
        }
        const blob = await assetResponse.blob();

        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
        setStatus(DiagramSvgStatus.READY);
      } catch (err) {
        if (cancelled) return;
        logger.error({
          loggerMsg: "useDiagramSvg: failed to resolve diagram SVG",
          projectId,
          err,
        });
        setStatus(DiagramSvgStatus.ERROR);
      }
    })();

    return () => {
      cancelled = true;
      // Revoke the blob URL so we don't leak object URLs across re-resolves,
      // project switches, or unmount.
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [projectId, refreshCounter]);

  return { url, status, refresh };
};
